import { zodResolver } from '@hookform/resolvers/zod';
import {
  createDeviceRequest,
  type DeviceCreatedResponse,
  type DeviceDto,
  type z,
} from '@zenarty/shared';
import { Ban, MonitorSmartphone, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import {
  Button,
  Callout,
  EmptyState,
  ErrorMessage,
  Field,
  KeyValue,
  OneTimeSecret,
  PageHeader,
  StatusBadge,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatDateTime, timeAgo } from '../lib/format';
import { nameMap, useAction, useDevices, useSites } from '../lib/queries';

export function DevicesPage() {
  const { can } = useAuth();
  const devices = useDevices();
  const sites = useSites(can('site:read'));
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<DeviceCreatedResponse | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const siteName = nameMap(sites.data?.items, (s) => `${s.name} (${s.code})`);

  const columns: Column<DeviceDto>[] = [
    {
      id: 'name',
      header: 'Scanner',
      sortValue: (d) => d.name,
      cell: (d) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <span className="icon-tile">
            <MonitorSmartphone />
          </span>
          <div>
            <div className="primary-cell mono">{d.name}</div>
            <div className="sub mono">{d.id.slice(0, 8)}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'site',
      header: 'Site',
      sortValue: (d) => siteName.get(d.siteId) ?? '',
      cell: (d) => siteName.get(d.siteId) ?? '—',
    },
    {
      id: 'status',
      header: 'Status',
      sortValue: (d) => d.status,
      cell: (d) => <StatusBadge status={d.status === 'pending' ? 'pending' : d.status} />,
    },
    {
      id: 'seen',
      header: 'Last used',
      sortValue: (d) => d.lastSeenAt ?? '',
      cell: (d) => <span className="muted">{d.lastSeenAt ? timeAgo(d.lastSeenAt) : 'Never'}</span>,
    },
    {
      id: 'added',
      header: 'Registered',
      sortValue: (d) => d.createdAt,
      cell: (d) => <span className="muted">{formatDate(d.createdAt)}</span>,
    },
  ];

  const open = devices.data?.items.find((d) => d.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Scanners"
        description="Handheld scanners (Zebra, Honeywell, iOS). A scanner is registered here, enrolled once with a one-time code, and then floor staff sign in on it with badge and PIN."
        actions={
          can('device:manage') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Register scanner
            </Button>
          ) : null
        }
      />
      {created ? (
        <EnrollmentNotice
          created={created}
          siteName={siteName.get(created.device.siteId)}
          onDismiss={() => setCreated(null)}
        />
      ) : null}
      <DataTable
        columns={columns}
        rows={devices.data?.items ?? []}
        rowKey={(d) => d.id}
        loading={devices.isLoading}
        searchPlaceholder="Search scanners"
        searchText={(d) => `${d.name} ${d.id} ${siteName.get(d.siteId) ?? ''}`}
        onRowClick={(d) => setOpenId(d.id)}
        initialSort={{ id: 'name' }}
        empty={
          <EmptyState
            icon={MonitorSmartphone}
            title="No scanners registered"
            action={
              can('device:manage') ? (
                <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                  Register a scanner
                </Button>
              ) : null
            }
          >
            Register each handheld before it is used on the floor. Lost or retired scanners can be
            revoked in one click.
          </EmptyState>
        }
      />
      {creating ? (
        <RegisterDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreated(r);
            setCreating(false);
          }}
        />
      ) : null}
      {open ? (
        <DeviceDrawer
          device={open}
          siteName={siteName.get(open.siteId)}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}

function EnrollmentNotice({
  created,
  siteName,
  onDismiss,
}: {
  created: DeviceCreatedResponse;
  siteName?: string;
  onDismiss: () => void;
}) {
  return (
    <div className="stack-sm" style={{ marginBottom: 16 }}>
      <OneTimeSecret
        label={`Enrolment code for ${created.device.name}${siteName ? ` at ${siteName}` : ''}`}
        value={created.enrollmentCode}
        note={`On the scanner, enter device ID ${created.device.id} and this code. It works once and expires ${formatDateTime(created.enrollmentExpiresAt)}.`}
      />
      <div>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function DeviceDrawer({
  device,
  siteName,
  onClose,
}: {
  device: DeviceDto;
  siteName?: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const revoke = useAction(() => api(`/devices/${device.id}/revoke`, { method: 'POST' }), {
    invalidate: [['devices']],
    success: `${device.name} revoked`,
    onSuccess: () => setConfirm(false),
  });
  return (
    <Drawer
      title={<span className="mono">{device.name}</span>}
      subtitle={siteName}
      icon={
        <span className="icon-tile">
          <MonitorSmartphone />
        </span>
      }
      onClose={onClose}
      footer={
        can('device:manage') && device.status !== 'revoked' ? (
          <Button variant="danger" icon={Ban} onClick={() => setConfirm(true)}>
            Revoke scanner
          </Button>
        ) : null
      }
    >
      {device.status === 'pending' ? (
        <Callout tone="warn" title="Waiting for enrolment">
          The one-time code was shown when the scanner was registered. If it was lost or expired,
          revoke this entry and register the scanner again.
        </Callout>
      ) : null}
      <section>
        <div className="section-title">Details</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={device.status} />],
            ['Device ID', <code key="i">{device.id}</code>],
            ['Site', siteName ?? '—'],
            ['Last used', device.lastSeenAt ? formatDateTime(device.lastSeenAt) : 'Never'],
            ['Registered', formatDateTime(device.createdAt)],
          ]}
        />
      </section>
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={device.id} />
        </section>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={`Revoke ${device.name}?`}
          confirmLabel="Revoke scanner"
          danger
          pending={revoke.isPending}
          onConfirm={() => revoke.mutate(undefined)}
          onClose={() => setConfirm(false)}
        >
          Anyone signed in on it is signed out immediately and it can never sign in again. Use this
          for lost, stolen or retired scanners.
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

function RegisterDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: DeviceCreatedResponse) => void;
}) {
  const sites = useSites();
  const form = useForm<
    z.input<typeof createDeviceRequest>,
    unknown,
    z.output<typeof createDeviceRequest>
  >({
    resolver: zodResolver(createDeviceRequest),
  });
  const create = useAction(
    (body: z.output<typeof createDeviceRequest>) =>
      api<DeviceCreatedResponse>('/devices', { method: 'POST', body }),
    {
      invalidate: [['devices']],
      success: 'Scanner registered',
      onSuccess: onCreated,
    },
  );
  const e = form.formState.errors;
  return (
    <Dialog
      title="Register a scanner"
      description="You'll get a one-time enrolment code to type into the scanner app."
      onClose={onClose}
      onSubmit={(ev) => void form.handleSubmit((v) => create.mutate(v))(ev)}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Register
          </Button>
        </>
      }
    >
      <ErrorMessage error={sites.error} />
      <Field label="Site" error={e.siteId?.message}>
        <select className="select" {...form.register('siteId')}>
          <option value="">Choose the site it works at…</option>
          {(sites.data?.items ?? [])
            .filter((s) => s.status === 'active')
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
        </select>
      </Field>
      <Field
        label="Scanner name"
        hint="Use the asset tag, e.g. PHX1-TC52-07"
        error={e.name?.message}
      >
        <input className="input mono" {...form.register('name')} />
      </Field>
    </Dialog>
  );
}
